type EventType = 'page_view' | 'report_generated'

interface TrackingPayload {
  type: EventType
  timestamp: string
  ref?: string
  metadata?: Record<string, string>
}

interface ReportMetadata {
  model?: string
  regions?: string[]
  mediaType?: string
  campaignType?: string
  fromCache?: boolean
}

class AnalyticsService {
  private readonly endpoint = '/api/analytics/track'
  private ref: string | null = null

  constructor() {
    // Parse ref parameter from URL on initialization
    this.ref = this.parseRefFromUrl()
  }

  private parseRefFromUrl(): string | null {
    try {
      const params = new URLSearchParams(window.location.search)
      return params.get('ref')
    } catch {
      return null
    }
  }

  getRef(): string | null {
    return this.ref
  }

  private async sendEvent(payload: TrackingPayload): Promise<void> {
    try {
      // Include ref in all payloads if present
      const payloadWithRef = this.ref
        ? { ...payload, ref: this.ref }
        : payload

      await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payloadWithRef),
      })
    } catch (error) {
      // Silently fail for analytics - don't disrupt user experience
      console.debug('Analytics tracking failed:', error)
    }
  }

  trackPageView(): void {
    this.sendEvent({
      type: 'page_view',
      timestamp: new Date().toISOString(),
      metadata: {
        url: window.location.href,
        referrer: document.referrer || 'direct',
      },
    })
  }

  trackReportGenerated(reportMetadata?: ReportMetadata): void {
    this.sendEvent({
      type: 'report_generated',
      timestamp: new Date().toISOString(),
      metadata: reportMetadata ? {
        model: reportMetadata.model || '',
        regions: reportMetadata.regions?.join(',') || '',
        mediaType: reportMetadata.mediaType || '',
        campaignType: reportMetadata.campaignType || '',
        fromCache: reportMetadata.fromCache ? 'true' : 'false',
      } : undefined,
    })
  }
}

export const analyticsService = new AnalyticsService()
